import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../api.js';
import { useAuth } from '../../auth/AuthProvider.js';
import type { Account, AccountNode } from '../../types/bookkeeping.js';

function flattenTree(nodes: AccountNode[]): Account[] {
  const result: Account[] = [];
  function walk(ns: AccountNode[]) {
    for (const n of ns) {
      result.push(n);
      if (n.children.length > 0) walk(n.children);
    }
  }
  walk(nodes);
  return result;
}

function daysAgo(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}

interface GLLine {
  entry_date: string;
  number: string;
  memo: string | null;
  line_id: string;
  line_number: number;
  currency: string;
  amount: string;
  fx_rate: string;
  base_amount: string;
  description: string | null;
  running_balance: string;
}

export function GeneralLedger() {
  const { t } = useTranslation('bookkeeping');
  const { activeOrgId } = useAuth();
  const [accountId, setAccountId] = useState<string>('');
  const [from, setFrom] = useState(daysAgo(30));
  const [to, setTo] = useState(new Date().toISOString().slice(0, 10));

  const { data: accountsData } = useQuery({
    queryKey: ['bookkeeping-accounts', activeOrgId],
    queryFn: () => api<{ tree: AccountNode[] }>('/bookkeeping/accounts'),
    enabled: !!activeOrgId,
  });
  const accounts = flattenTree(accountsData?.tree ?? []);

  const { data, isLoading, error } = useQuery({
    queryKey: ['general-ledger', activeOrgId, accountId, from, to],
    queryFn: () =>
      api<{ account_id: string; from: string; to: string; lines: GLLine[] }>(
        `/bookkeeping/reports/general-ledger?account_id=${accountId}&from=${from}&to=${to}`,
      ),
    enabled: !!activeOrgId && !!accountId && !!from && !!to,
  });

  const selectedAccount = accounts.find((a) => a.id === accountId);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">{t('generalLedger.title')}</h1>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className="block text-xs text-slate-500">
            {t('generalLedger.account')}
            <select
              value={accountId}
              onChange={(e) => setAccountId(e.target.value)}
              className="rounded border px-2 py-1 text-sm"
            >
              <option value="">— {t('generalLedger.selectAccount')} —</option>
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.code} {a.name} ({a.currency})
                </option>
              ))}
            </select>
          </label>
        </div>
        <div>
          <label className="block text-xs text-slate-500">
            {t('generalLedger.from')}
            <input
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className="rounded border px-2 py-1 text-sm"
            />
          </label>
        </div>
        <div>
          <label className="block text-xs text-slate-500">
            {t('generalLedger.to')}
            <input
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="rounded border px-2 py-1 text-sm"
            />
          </label>
        </div>
      </div>

      {!accountId && (
        <p className="text-sm text-slate-400">{t('generalLedger.selectAccountPrompt')}</p>
      )}
      {isLoading && <p className="text-sm text-slate-500">{t('common:loading')}</p>}
      {error && (
        <p className="text-sm text-red-600">
          {(error as { message?: string }).message ?? 'Error al cargar libro mayor'}
        </p>
      )}

      {data && (
        <div className="overflow-x-auto rounded border bg-white">
          <div className="border-b px-4 py-2 text-sm font-medium">
            {selectedAccount ? `${selectedAccount.code} — ${selectedAccount.name}` : accountId}
            <span className="ml-2 text-xs text-slate-400">
              {from} → {to}
            </span>
          </div>
          <table className="w-full text-sm">
            <thead className="border-b text-left text-xs uppercase text-slate-500">
              <tr>
                <th className="px-2 py-2">{t('generalLedger.columns.date')}</th>
                <th className="px-2 py-2">{t('generalLedger.columns.je')}</th>
                <th className="px-2 py-2">{t('generalLedger.columns.memo')}</th>
                <th className="px-2 py-2">{t('generalLedger.columns.description')}</th>
                <th className="px-2 py-2 text-right">{t('generalLedger.columns.amount')}</th>
                <th className="px-2 py-2 text-right">
                  {t('generalLedger.columns.runningBalance')}
                </th>
              </tr>
            </thead>
            <tbody>
              {data.lines.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-3 py-4 text-center text-slate-400">
                    {t('generalLedger.empty')}
                  </td>
                </tr>
              ) : (
                data.lines.map((line) => (
                  <tr key={line.line_id} className="border-b last:border-0 hover:bg-slate-50">
                    <td className="px-2 py-1.5">{line.entry_date}</td>
                    <td className="px-2 py-1.5 font-mono text-xs">
                      JE-{String(line.number).padStart(6, '0')}
                    </td>
                    <td className="px-2 py-1.5 text-slate-600">{line.memo ?? '—'}</td>
                    <td className="px-2 py-1.5 text-slate-500">{line.description ?? '—'}</td>
                    <td className="px-2 py-1.5 text-right font-mono text-xs">{line.base_amount}</td>
                    <td className="px-2 py-1.5 text-right font-mono text-xs font-medium">
                      {line.running_balance}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
